import React from "react";
import { MdCheck, MdExpandMore } from "react-icons/md";
import { menuPanelClass } from "./DropdownMenu";
import useCollision from "./useCollision";

/*
 * Kuest select (components/ui/select.tsx) as a small listbox:
 * trigger  rounded-md border bg-transparent py-2 pl-2.5 pr-2 text-sm + chevron;
 * list     the dropdown panel (ring-1 ring-foreground/10, shadow-md, min-w-36);
 * option   pl-2 pr-8 py-1.5 with a check at the right of the selected one.
 * onChange receives the chosen option's value.
 * Keyboard: Enter/Space/ArrowDown open, arrows/Home/End move, Enter selects, Esc closes
 * back to the trigger, typing a letter jumps to the next matching option.
 */
export const selectTriggerClass =
  "flex h-9 w-full min-w-0 items-center justify-between gap-1.5 whitespace-nowrap rounded-md border border-border bg-transparent py-2 pl-2.5 pr-2 text-sm text-foreground outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30 dark:hover:bg-input/50";

export default function Select({ value, onChange, options, placeholder = "Select…", ariaLabel, id, className = "", align = "start", size = "default", disabled }) {
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(-1);
  const wrapperRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const listRef = React.useRef(null);
  const listId = React.useId();
  useCollision(listRef, open, align);
  const selectedIndex = options.findIndex((o) => String(o.value) === String(value ?? ""));
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

  React.useEffect(() => {
    if (!open) return undefined;
    function onDown(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open || active < 0 || !listRef.current) return;
    const node = listRef.current.querySelector(`[data-index="${active}"]`);
    if (node && node.scrollIntoView) node.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  function openList() {
    setActive(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  }

  function choose(index) {
    const option = options[index];
    if (!option || option.disabled) return;
    setOpen(false);
    triggerRef.current?.focus();
    if (String(option.value) !== String(value ?? "")) onChange(option.value);
  }

  function move(delta) {
    if (!options.length) return;
    let next = active;
    for (let i = 0; i < options.length; i++) {
      next = (next + delta + options.length) % options.length;
      if (!options[next].disabled) break;
    }
    setActive(next);
  }

  function onKeyDown(event) {
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
        event.preventDefault();
        openList();
      }
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      move(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      move(-1);
    } else if (event.key === "Home") {
      event.preventDefault();
      setActive(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setActive(options.length - 1);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      choose(active);
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    } else if (event.key === "Tab") {
      setOpen(false);
    } else if (event.key.length === 1 && /\S/.test(event.key)) {
      const ch = event.key.toLowerCase();
      for (let i = 1; i <= options.length; i++) {
        const idx = (active + i) % options.length;
        if (String(options[idx].label).toLowerCase().startsWith(ch)) {
          setActive(idx);
          break;
        }
      }
    }
  }

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
        className={`${selectTriggerClass} ${size === "sm" ? "h-8" : ""}`}
      >
        <span className={`truncate ${selected ? "" : "text-muted-foreground"}`}>{selected ? selected.label : placeholder}</span>
        <MdExpandMore className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-150 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          data-state="open"
          className={`wasaa-menu absolute top-[calc(100%+4px)] z-[70] max-h-72 min-w-[9rem] max-w-[calc(100vw-2rem)] overflow-y-auto ring-1 ring-foreground/10 ${
            align === "end" ? "right-0 origin-top-right" : "left-0 origin-top-left"
          } w-max min-w-full ${menuPanelClass}`}
        >
          {options.map((option, index) => {
            const isSelected = index === selectedIndex;
            return (
              <div
                key={`${option.value}`}
                id={`${listId}-${index}`}
                data-index={index}
                role="option"
                aria-selected={isSelected}
                aria-disabled={option.disabled ? "true" : undefined}
                onMouseEnter={() => setActive(index)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(index)}
                className={`relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none ${
                  index === active ? "bg-accent text-accent-foreground" : ""
                } ${option.disabled ? "pointer-events-none opacity-50" : ""}`}
              >
                <span className="max-w-[28rem] truncate">{option.label}</span>
                {isSelected && (
                  <span className="pointer-events-none absolute right-2 flex h-4 w-4 items-center justify-center">
                    <MdCheck className="h-4 w-4" />
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
